import { CallTypes } from "@sprycore/spry-api-client"
import SessionClient from "./SessionClient"

type ParticipantDataArgs = Omit<CallTypes.AddParticipantDataArgs, "campaignKey" | "sessionKey" | "flowData"> & { extraApiData?: any }

export default class ParticipantBuilder {
    __sessionClient: SessionClient
    __participant: ParticipantDataArgs = { metadata: {}, extraApiData: {}}

    constructor(sessionClient: SessionClient) {
        this.__sessionClient = sessionClient
    }

    get sessionClient() { return this.__sessionClient }
    get participant() { return this.__participant }

    /** Merges participant data with existing data */
    add(dataToMerge: ParticipantDataArgs) {
        Object.keys(dataToMerge).forEach(x => {
            const value = (dataToMerge as any)[x]
            if (value) {
                if (x === "metadata" && typeof value !== "object") { throw new Error("metadata must be an object type") }
                else if (x === "extraApiData" && typeof value !== "object") { throw new Error("extraApiData must be an object type") }
                else if (typeof value !== "string") { throw new Error(`${x} must be a string`) }
                (this.__participant as any)[x] = (dataToMerge as any)[x]
            }
            else {
                delete (this.__participant as any)[x]
            }
        })
        return this
    }

    /** Metadata to be saved as part of the participant's profile data */
    get metadata() { return this.__participant.metadata }
    /** Metadata to be saved as part of the participant's profile data */
    addMetadata(metadata: any | null | undefined) {
        if (metadata) { 
            if (typeof metadata !== "object") { throw new Error("metadata must be an object type") }
            this.__participant.metadata = metadata
        }
        else { this.__participant.metadata = {}}
        return this
    }

    /** Extra data which is passed to the API, but which is NOT saved as part of the participant's profile data */
    get extraApiData() { return this.__participant.extraApiData }
    /** Extra data which is passed to the API, but which is NOT saved as part of the participant's profile data */
    set extraApiData(extraApiData: any | null | undefined) { this.setExtraApiData(extraApiData) }
    /** Extra data which is passed to the API, but which is NOT saved as part of the participant's profile data */
    setExtraApiData(extraApiData: any | null | undefined) {
        if (extraApiData) { 
            if (typeof extraApiData !== "object") { throw new Error("extraApiData must be an object type") }
            this.__participant.extraApiData = extraApiData
        }
        else { this.__participant.extraApiData = {}}
        return this
    }
    
    /** Participant's first name */
    get firstName() { return this.__participant.firstName }
    /** Participant's first name */
    set firstName(firstName: string | null | undefined) { this.setFirstName(firstName) }
    /** Participant's first name */
    setFirstName(firstName: string | null | undefined) { 
        if (firstName) { this.__participant.firstName = firstName }
        else { delete this.__participant.firstName }
        return this
    }
    
    /** Participant's last name */
    get lastName() { return this.__participant.lastName }
    /** Participant's last name */
    set lastName(lastName: string | null | undefined) { this.setLastName(lastName) }
    /** Participant's last name */
    setLastName(lastName: string | null | undefined) { 
        if (lastName) { this.__participant.lastName = lastName }
        else { delete this.__participant.lastName }
        return this
    }
    
    /** Participant's email address */
    get email() { return this.__participant.email }
    /** Participant's email address */
    set email(email: string | null | undefined) { this.setEmail(email) }
    /** Participant's email address */
    setEmail(email: string | null | undefined) { 
        if (email) { this.__participant.email = email }
        else { delete this.__participant.email }
        return this
    }
    
    /** Participant's phone number */
    get phone() { return this.__participant.phone }
    /** Participant's phone number */
    set phone(phone: string | null | undefined) { this.setPhone(phone) }
    /** Participant's phone number */
    setPhone(phone: string | null | undefined) { 
        if (phone) { this.__participant.phone = phone }
        else { delete this.__participant.phone }
        return this
    }
    
    /** Participant's address */
    get address() { return this.__participant.address }
    /** Participant's address */
    set address(address: string | null | undefined) { this.setAddress(address) }
    /** Participant's address */
    setAddress(address: string | null | undefined) { 
        if (address) { this.__participant.address = address }
        else { delete this.__participant.address }
        return this 
    }
    
    /** Participant's address (line 2) */
    get address2() { return this.__participant.address2 }
    /** Participant's address (line 2) */
    set address2(address2: string | null | undefined) { this.setAddress2(address2) }
    /** Participant's address (line 2) */
    setAddress2(address2: string | null | undefined) { 
        if (address2) { this.__participant.address2 = address2 }
        else { delete this.__participant.address2 }
        return this
    }
    
    /** Participant's city */
    get city() { return this.__participant.city }
    /** Participant's city */
    set city(city: string | null | undefined) { this.setCity(city) }
    /** Participant's city */
    setCity(city: string | null | undefined) { 
        if (city) { this.__participant.city = city }
        else { delete this.__participant.city }
        return this
    }
    
    /** Participant's province (or state) */
    get province() { return this.__participant.province }
    /** Participant's province (or state) */
    set province(province: string | null | undefined) { this.setProvince(province) }
    /** Participant's province (or state) */
    setProvince(province: string | null | undefined) { 
        if (province) { this.__participant.province = province }
        else { delete this.__participant.province }
        return this
    }
    
    /** Participant's postal code (or zip code) */
    get postal() { return this.__participant.postal }
    /** Participant's postal code (or zip code) */
    set postal(postal: string | null | undefined) { this.setPostal(postal) }
    /** Participant's postal code (or zip code) */
    setPostal(postal: string | null | undefined) { 
        if (postal) { this.__participant.postal = postal }
        else { delete this.__participant.postal }
        return this
    }
    
    /** Participant's country */
    get country() { return this.__participant.country }
    /** Participant's country */
    set country(country: string | null | undefined) { this.setCountry(country) }
    /** Participant's country */
    setCountry(country: string | null | undefined) { 
        if (country) { this.__participant.country = country }
        else { delete this.__participant.country }
        return this
    }
    
    /** Participant's preferred language (two-letter lowercase language code) */
    get preferredLanguage() { return this.__participant.country }
    /** Participant's preferred language (two-letter lowercase language code) */
    set preferredLanguage(preferredLanguage: string | null | undefined) { this.setPreferredLanguage(preferredLanguage) }
    /** Participant's preferred language (two-letter lowercase language code) */
    setPreferredLanguage(preferredLanguage: string | null | undefined) { 
        if (preferredLanguage) { this.__participant.preferredLanguage = preferredLanguage }
        else { delete this.__participant.preferredLanguage }
        return this
    }

    /** Call an API method, passing the participant data plus optional extra data */
    async call<T>(method: string): Promise<T> {
        const args = Object.assign({}, this.__participant) as ParticipantDataArgs 
        if (args.metadata && !Object.keys(args.metadata)) { delete args.metadata }
        return await this.__sessionClient.call<T>(method, args)
    }
}